/**
 * t294u: the one date range filter. A compact button showing the choice ("Last 7 days") that opens a small menu of
 * presets; Custom range shows the two dates. Inside a phone Filters sheet the presets are plain chips and the custom
 * dates are calendar buttons, so the keyboard never opens. Days are UK days (Europe/London), weeks start on Monday
 * (lib/dateRange). Each screen picks its presets and default; useDateRangeParam keeps the choice in the URL.
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { CalendarDays, ChevronDown } from 'lucide-react';
import {
  ALL_PRESETS, LONDON, PRESET_LABELS, formatRangeParam, parseRange, presetDays, presetValue, rangeLabel,
  type DateRangeValue, type RangePreset,
} from '@/lib/dateRange';
import { useNarrow } from '@/lib/useMedia';
import { DatePickerButton } from './DateTimeFields';
import { FilterSheetContext, Input } from './index';

export type { DateRangeValue, RangePreset } from '@/lib/dateRange';

/** The active choice: the brand yellow fill (bg-accent) with black text (never yellow text). */
const ACTIVE = 'bg-accent font-semibold text-on-accent';
const short = (iso: string) => (iso ? DateTime.fromISO(iso, { zone: LONDON }).toFormat('ccc d LLL yyyy') : '');

interface Props {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  /** The presets this screen offers, in order. Default: every preset with Custom range last. */
  presets?: RangePreset[];
  /** Accessible name of the control. */
  label?: string;
  /** t329u: a screen's own names for presets (My checklists says "All" for Any time). */
  labels?: Partial<Record<RangePreset, string>>;
  /** Force the inline chips (phone sheets) or the button and menu; default: chips inside a phone Filters sheet. */
  inline?: boolean;
  /** 'sm' matches the h-8 filter rows (Audit log). */
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string;
}

/** Starting days for Custom range: the current days, else the last 7 days. */
function customStart(v: DateRangeValue) {
  return v.from && v.to ? { from: v.from, to: v.to } : presetDays('last-7-days')!;
}
function customValue(from: string, to: string): DateRangeValue {
  return from > to ? { preset: 'custom', from: to, to: from } : { preset: 'custom', from, to };
}

export function DateRangeFilter({ value, onChange, presets = ALL_PRESETS, label = 'Dates', labels, inline, size, className, 'data-testid': testId = 'date-range' }: Props) {
  const inSheet = useContext(FilterSheetContext);
  if (inline ?? inSheet) return <DateRangePresets value={value} onChange={onChange} presets={presets} label={label} labels={labels} className={className} data-testid={testId} />;
  return <DateRangeMenu value={value} onChange={onChange} presets={presets} label={label} labels={labels} size={size} className={className} data-testid={testId} />;
}

function DateRangeMenu({ value, onChange, presets = ALL_PRESETS, label = 'Dates', labels, size = 'md', className, 'data-testid': testId }: Props) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(value.preset === 'custom');
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const narrow = useNarrow();
  useEffect(() => { if (open) setCustom(value.preset === 'custom'); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Element | null;
      // The custom dates' calendars open in a portal: picking a day there is not an outside click.
      if (!t || wrap.current?.contains(t) || t.closest?.('[data-testid=date-picker],[role=dialog][aria-label="Choose a date"]')) return;
      setOpen(false);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[data-testid=date-picker]')) { setOpen(false); button.current?.focus(); } };
    document.addEventListener('mousedown', down); document.addEventListener('touchstart', down); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('touchstart', down); document.removeEventListener('keydown', key); };
  }, [open]);
  const pick = (p: RangePreset) => {
    if (p === 'custom') { setCustom(true); const s = customStart(value); if (value.preset !== 'custom') onChange(customValue(s.from, s.to)); return; }
    onChange(presetValue(p)); setCustom(false); setOpen(false); button.current?.focus();
  };
  const days = customStart(value);
  return (
    <div ref={wrap} className={clsx('relative inline-block', className)} data-testid={testId}>
      <button ref={button} type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`${label}: ${rangeLabel(value, undefined, labels)}`} onClick={() => setOpen(o => !o)}
        className={clsx('inline-flex items-center', size === 'sm' ? 'h-8' : 'h-9', ' gap-1.5 whitespace-nowrap rounded-md bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-black dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-800')}
        data-testid={`${testId}-button`}>
        <CalendarDays className="h-4 w-4 text-slate-500" aria-hidden />{rangeLabel(value, undefined, labels)}<ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
      </button>
      {open && (
        <div className="on-page absolute left-0 z-40 mt-1 w-56 rounded-lg bg-white py-1 shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700" data-testid={`${testId}-menu`}>
          <div role="menu" aria-label={label}>
            {presets.map(p => {
              const on = p === 'custom' ? custom || value.preset === 'custom' : !custom && value.preset === p;
              return (
                <button key={p} type="button" role="menuitemradio" aria-checked={on} onClick={() => pick(p)} data-testid={`${testId}-${p}`}
                  className={clsx('flex w-full items-center px-3 py-1.5 text-left text-sm', on ? ACTIVE : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700')}>
                  {labels?.[p] ?? PRESET_LABELS[p]}
                </button>
              );
            })}
          </div>
          {custom && (
            <div className="space-y-2 border-t border-slate-100 px-3 pb-2 pt-2 dark:border-slate-700" data-testid={`${testId}-custom-days`}>
              {narrow ? <CustomButtons from={days.from} to={days.to} onChange={onChange} /> : <>
                <Input type="date" aria-label="From" value={days.from} max={days.to} onChange={e => e.target.value && onChange(customValue(e.target.value, days.to))} />
                <Input type="date" aria-label="To" value={days.to} min={days.from} onChange={e => e.target.value && onChange(customValue(days.from, e.target.value))} />
              </>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Calendar buttons for the custom days (no text field, so no keyboard on a phone). */
function CustomButtons({ from, to, onChange }: { from: string; to: string; onChange: (v: DateRangeValue) => void }) {
  const cls = 'flex h-10 w-full items-center gap-2 rounded-md bg-white px-3 text-left text-sm text-slate-800 ring-1 ring-inset ring-slate-300 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700';
  return (
    <div className="grid grid-cols-1 gap-2">
      <DatePickerButton value={from} onPick={d => onChange(customValue(d, to))} className={cls} aria-label={`From ${short(from)}`} title="From">
        <CalendarDays className="h-4 w-4 text-slate-500" aria-hidden /><span className="text-slate-500">From</span><span className="ml-auto tabular-nums">{short(from)}</span>
      </DatePickerButton>
      <DatePickerButton value={to} onPick={d => onChange(customValue(from, d))} className={cls} aria-label={`To ${short(to)}`} title="To">
        <CalendarDays className="h-4 w-4 text-slate-500" aria-hidden /><span className="text-slate-500">To</span><span className="ml-auto tabular-nums">{short(to)}</span>
      </DatePickerButton>
    </div>
  );
}

/** The presets as chips, for a phone Filters sheet; Custom range shows two calendar buttons. */
export function DateRangePresets({ value, onChange, presets = ALL_PRESETS, label = 'Dates', labels, className, 'data-testid': testId = 'date-range' }: Props) {
  const [custom, setCustom] = useState(value.preset === 'custom');
  const pick = (p: RangePreset) => {
    if (p === 'custom') { setCustom(true); const s = customStart(value); if (value.preset !== 'custom') onChange(customValue(s.from, s.to)); return; }
    setCustom(false); onChange(presetValue(p));
  };
  const days = customStart(value);
  return (
    <div className={clsx('space-y-2', className)} data-testid={`${testId}-presets`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400" id={`${testId}-label`}>{label}</div>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby={`${testId}-label`}>
        {presets.map(p => {
          const on = p === 'custom' ? custom || value.preset === 'custom' : !custom && value.preset === p;
          return (
            <button key={p} type="button" role="radio" aria-checked={on} onClick={() => pick(p)} data-testid={`${testId}-${p}`}
              className={clsx('h-9 rounded-full px-3 text-sm ring-1 ring-inset', on ? `${ACTIVE} ring-black/10` : 'bg-white text-slate-700 ring-slate-300 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700')}>
              {labels?.[p] ?? PRESET_LABELS[p]}
            </button>
          );
        })}
      </div>
      {(custom || value.preset === 'custom') && <CustomButtons from={days.from} to={days.to} onChange={onChange} />}
    </div>
  );
}

/**
 * The range in the URL (?range=last-7-days or ?range=2026-10-01..2026-10-14). `legacy` reads an older link's own
 * params (e.g. ?start=&end=) when there is no ?range=; those params are dropped on the next change. The default is
 * left out of the URL.
 */
export function useDateRangeParam({ key = 'range', presets = ALL_PRESETS, fallback, legacy, legacyKeys = [] }: {
  key?: string; presets?: RangePreset[]; fallback: Exclude<RangePreset, 'custom'>;
  legacy?: (sp: URLSearchParams) => DateRangeValue | null; legacyKeys?: string[];
}): [DateRangeValue, (v: DateRangeValue) => void, boolean] {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get(key);
  const today = DateTime.now().setZone(LONDON).toISODate();
  const legacyValue = raw == null && legacy ? legacy(sp) : null;
  const legacyKey = legacyValue ? formatRangeParam(legacyValue) : '';
  const value = useMemo(() => (raw == null && legacyValue ? legacyValue : parseRange(raw, presets, fallback)),
    [raw, legacyKey, presets.join(), fallback, today]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = useCallback((v: DateRangeValue) => {
    setSp(prev => {
      const next = new URLSearchParams(prev);
      legacyKeys.forEach(k => next.delete(k));
      if (v.preset === fallback) next.delete(key); else next.set(key, formatRangeParam(v));
      return next;
    }, { replace: true });
  }, [setSp, key, fallback, legacyKeys.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  return [value, set, value.preset === fallback];
}
